import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from "@angular/core";
import { Router } from '@angular/router';
import { UserPreferencesStore } from '@app/shared/stores/user-preferences.store';
import { environment } from '@env/environment';
import { PriceSettingEnum } from "@infrastructure/price-setting.enum";
import { FrameResponseDataModel } from '@models/api-models/FMResponseModel';
import { PriceSettingModel } from "@models/price-setting.model";
import { UserDataModel } from "@models/user-data-model";
import { AppModeEnum, AppState } from "@shared/app-state";
import { CookieService } from "ngx-cookie-service";
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { AppConstants } from '../app.constants';
import { AftersalesService, IAftersalesData } from './aftersales.service';
import { GoogleAnalyticsService } from "./google-analytics.service";
import { OrderOnBehalfService } from './order-on-behalf.service';
import { PriceSettingsService } from "./price-settings.service";
import { SessionService } from './session.service';
import { UserService } from './user.service';

@Injectable({
	providedIn: 'root'
})
export class LoginService {

	userPreferencesStore = inject(UserPreferencesStore);

	private readonly tokenId: string = "dcg-token";

	private isLoggedInSubject: BehaviorSubject<boolean>;
	private loggedInUserSubject = new BehaviorSubject<UserDataModel | null>(null);

	constructor(
		private storage: CookieService,
		private sessionStorage: SessionService,
		private userservice: UserService,
		private orderOnBehalfService: OrderOnBehalfService,
		private googleAnalyticsService: GoogleAnalyticsService,
		private appState: AppState,
		private priceSettingsService: PriceSettingsService,
		private http: HttpClient,
		private router: Router,
		private userService: UserService,
		private AftersalesService: AftersalesService
	) {
		this.isLoggedInSubject = new BehaviorSubject<boolean>(this.storage.get(this.tokenId) && this.userservice.currentUser$.value ? true : false);
	}

	public async Login(token: string) {
		await this.Logout(false);
		this.createLoginCookie(token);
		await this.setUserData();

		try {
			const user = await this.userservice.GetUser();

			if (user) {
				await this.googleAnalyticsService.eventEmitter('login', 'engagement', `${user.Uuid} has logged in`, 40);

				this.appState.priceModuleEnabled.next(user.priceModuleEnabled && user.PriceSettingAllowed !== PriceSettingEnum.AllowNone);
				
				try {
					await this.userPreferencesStore.setContactCode(user.ContactCode);
					const userPreferencePriceDisplay = this.userPreferencesStore.priceDisplay();
					if (userPreferencePriceDisplay) {
						this.appState.priceSetting.next(new PriceSettingModel(userPreferencePriceDisplay, this.priceSettingsService.getPriceText(userPreferencePriceDisplay)));
					}
					else {
						this.appState.priceSetting.next(new PriceSettingModel(PriceSettingEnum.AllowNone, this.priceSettingsService.getPriceText(PriceSettingEnum.AllowNone)));
					}
				}
				catch (error) {
					//user probably not logged in
				}
			}
		}
		catch (error) {
		}

		this.isLoggedInSubject.next(true);

		this.AftersalesService.CheckAndPerformAftersalesFlow(token).then((aftersalesData: IAftersalesData | undefined) => {
			if (aftersalesData?.FlowType === AppModeEnum.repair) {
				setTimeout(async () => {
					const user = await this.userService.GetUser();
					let requestModel = { 'userName': user?.Name, 'serialNumber': aftersalesData.SerialNumber };

					let frameResponse: FrameResponseDataModel = await firstValueFrom(this.http.post<FrameResponseDataModel>(environment.dataUrl + '/parts/framerequest', requestModel, { headers: new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8') }));

					if (frameResponse) {
						let pd = frameResponse.ProductDescriptions.find(pd => pd.LanguageId === 'en-gb');

						if (pd) {
							pd.LanguageId = 'en';
						}

						await this.router.navigateByUrl('parts/model', { state: { data: frameResponse } });
					}
				});
			}
			else {
				this.router.navigate(['/']);
			}
		});
	}

	public async SetTokenAndUsername(token: string) {
		if (token) {
			await this.Login(token);
		}
	}

	public async liteLogin(token: string) {
		await this.Logout(false);

		this.createLoginCookie(token, true);

		await this.setUserData(true);

		setTimeout(async () => {
			console.warn('timed logout for liteLogin');
			await this.Logout();
		}, 3000);
	}

	public createLoginCookie(token: string, isLiteLogin: boolean = false) {
		const ttl = new Date();

		if (isLiteLogin) {
			ttl.setTime(new Date().getTime() + 3 * 1000); // add 30 second cookie
		} else {
			ttl.setTime(new Date().getTime() + 14 * 24 * 60 * 60 * 1000); // add 14 day cookie
		}

		const useSecure = environment.production;
		this.storage.set(this.tokenId, token, ttl, '/', this.getHostname, useSecure, "Lax");
	}

	private async setUserData(isLiteLogin: boolean = false): Promise<void> {
		const user = await this.userservice.getUserData();

		user.IsLiteLogin = isLiteLogin;
		user.LoginRegistered = new Date();
		this.userservice.setUser(user);
		this.orderOnBehalfService.updateOrderRelevantData();
		this.loggedInUserSubject.next(user);
	}

	public async liteLogout() {
		this.isLoggedInSubject.next(false);

		if (this.storage.get(this.tokenId) != null) {
			this.storage.delete(this.tokenId, '/', this.getHostname);
		}

		this.loggedInUserSubject.next(null);
		this.sessionStorage.clear();
	}

	public async Logout(forceClearAll = true) {
		const user = await this.userservice.currentUser$.getValue();

		this.isLoggedInSubject.next(false);
		this.loggedInUserSubject.next(null);

		if (this.storage.get(this.tokenId) != null) {
			this.storage.delete(this.tokenId, '/', this.getHostname);
		}

		this.userservice.removeUserDataFromLocalStorage();

		this.orderOnBehalfService.resetCustomer();
		this.sessionStorage.clear();

		if (user) {
			await this.googleAnalyticsService.eventEmitter('logout', 'engagement', `${user.Uuid} has logged out`, 20);
		}
	}

	public GetToken(): string {
		return this.storage.get(this.tokenId);
	}

	public async PreserveNotifications(): Promise<void> {
	}

	public IsLoggedInObservable(): Observable<boolean> {
		return this.isLoggedInSubject.asObservable();
	}

	public LoggedInUserObservable() {
		return this.loggedInUserSubject.asObservable();
	}

	public IsLoggedIn() {
		return this.isLoggedInSubject.value;
	}

	public AllowUnauthenticatedAccess() {
		let allowUnauthenticatedAccess = false;

		AppConstants.allowedUnauthenticatedUrlParts.forEach(urlPart => {
			if (window.location.href.indexOf(urlPart) > -1) {
				allowUnauthenticatedAccess = true;
			}
		});

		return allowUnauthenticatedAccess;
	}

	private get getHostname() {
		return window.location.hostname == "localhost" ? "" : window.location.hostname;
	}

	public async postLogin(username: string, password: string): Promise<any> {
		const body = `grant_type=password&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`;

		return await firstValueFrom(this.http.post<any>(`${environment.dataUrl}/token`, body, {
			headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded; charset=utf-8')
		}));
	}

	public async postLiteLogin(userUuid: string): Promise<any> {
		const body = `"${encodeURIComponent(userUuid)}"`;
		return await firstValueFrom(this.http.post<any>(`${environment.dataUrl}/litetoken`, body, {
			headers: new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8')
		}));
	}
}